import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { MDText } from 'i18n-react';
import Link from 'next/link';

import { ArrowDownIcon, ArrowUpIcon, Button, HomeIcon } from '@motorway/mw-highway-code';
import { Chip } from '@motorway/mw-highway-code/pre-alpha';

import { useInsightsRouter } from 'Components/Insights/hooks/useInsightsRouter';
import { useBreakpoint } from 'Context/BreakpointsContext/BreakpointsContext';
import { triggerInsightsHomeButtonEvent } from 'Services/analytics/events';
import Drawer from 'Storybook/core/Drawer/Drawer';
import { Popover } from 'Storybook/core/Popover';
import { Variant } from 'Types/variant';
import ROUTES from 'Utilities/routes';

import LocalTexts from '../../../Insights.texts';

import { MakeAndModelForm } from './components/MakeAndModelForm';

import styles from './Topbar.module.scss';

const LocalT = new MDText(LocalTexts);

export const Topbar: FC = () => {
	const [isOpen, setIsOpen] = useState(false);

	const { vehicle } = useInsightsRouter();

	const { make, model } = vehicle || {};

	useEffect(() => {
		setIsOpen(false);
	}, [make, model]);

	const {
		maxWidth: { breakpointMobile, breakpointTablet },
	} = useBreakpoint();

	const onChipClick = () => {
		setIsOpen(!isOpen);
	};

	const handleDrawerClose = () => {
		setIsOpen(false);
	};

	const chipComponent = (
		<Chip
			selected
			icon={isOpen ? ArrowUpIcon : ArrowDownIcon}
			id="insights-make-and-model-chip"
			label={LocalT.translate('filters.makeAndModel')}
			onClick={onChipClick}
			size={breakpointTablet ? 'small' : 'medium'}
		/>
	);

	return (
		<div className={styles.container}>
			<div className={styles.controlsContainer}>
				<Link legacyBehavior passHref href={ROUTES.INSIGHTS_LANDING.as()}>
					<Button
						as="a"
						className={styles.homeButton}
						icon={HomeIcon}
						label="Home"
						onClick={() => {
							triggerInsightsHomeButtonEvent();
						}}
						showLabel={false}
						size={breakpointTablet ? 'small' : 'medium'}
						variant={'secondary'}
					/>
				</Link>
				{!breakpointMobile && (
					<Popover
						modal
						content={<MakeAndModelForm isParentOpen={isOpen} />}
						contentAlign="start"
						contentClassName={styles.popoverContent}
						contentSide="bottom"
						contentSideOffset={16}
						onOpenChange={setIsOpen}
						open={isOpen}
						trigger={<div className={styles.chipWrap}>{chipComponent}</div>}
					/>
				)}
				{breakpointMobile && (
					<>
						{chipComponent}
						<Drawer
							data-testid="insights-make-and-model-drawer"
							direction="bottom"
							isOpen={isOpen}
							onClose={handleDrawerClose}
							size="fit-content"
							variant={Variant.SECONDARY}
						>
							<MakeAndModelForm isParentOpen={isOpen} />
						</Drawer>
					</>
				)}
			</div>
		</div>
	);
};
